import type { PluginManifest, PluginSettingDef } from "@d12labs/plugins/types";

// Mirrors the option ids of MEETING_PERF_MODEL_OPTIONS in
// packages/@d12labs/ai/src/profiles.ts — mirrored, not imported, because this manifest
// is client-bundled and must not pull in the server AI SDKs. A drift test
// (plugins/meeting-perf/__tests__/manifest.test.ts) keeps the two lists equal.
const MODEL_OPTIONS: NonNullable<PluginSettingDef["options"]> = [
	{ value: "glm-5.2", label: "GLM-5.2 (z.ai) — default" },
	{ value: "glm-5.3", label: "GLM-5.3 (z.ai)" },
	{ value: "glm-5.3-flash", label: "GLM-5.3 Flash (z.ai)" },
	{ value: "glm-5.3-flashx", label: "GLM-5.3 FlashX (z.ai)" },
	{ value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Anthropic)" },
	{ value: "claude-sonnet-5", label: "Claude Sonnet 5 (Anthropic)" },
];

const MODEL_CAVEATS =
	"Default glm-5.2. glm-5.3 and glm-5.3-flash run close to the 240s remote timeout on long transcripts. The Claude options need ANTHROPIC_API_KEY. Takes effect on the next call — no restart.";

export const manifest: PluginManifest = {
	name: "meeting-perf",
	label: "Meeting Performance",
	description:
		"Analyze meeting transcripts for per-speaker communication performance, scored across six KPIs, with trends over time.",
	version: "0.1.0",
	navItems: [
		{ label: "Meetings", href: "/meeting-perf", icon: "Presentation" },
		{ label: "Coaching", href: "/meeting-perf/coaching", icon: "Sparkles" },
		{ label: "Activity", href: "/meeting-perf/activity", icon: "Activity" },
	],
	settings: {
		id: "meeting-perf",
		title: "Meeting Performance",
		icon: "Plug",
		blurb:
			'Fireflies meeting ingestion, the weekly coaching nudge, and which AI model drives each task. Fireflies is opt-in — blank disables the webhook (it returns "not configured" and ingestion no-ops). The model selects fall back to glm-5.2 when unset.',
		rows: [
			{
				key: "FIREFLIES_WEBHOOK_SECRET",
				label: "Fireflies webhook secret",
				secret: true,
				// biome-ignore lint/suspicious/noTemplateCurlyInString: literal operator copy — shown verbatim, not interpolated
				help: "HMAC secret to verify inbound webhooks. Point Fireflies at POST ${BETTER_AUTH_URL}/api/plugins/meeting-perf/webhooks/fireflies.",
			},
			{
				key: "FIREFLIES_API_KEY",
				label: "Fireflies API key",
				secret: true,
				help: "Fetches the transcript (and audio/video, on plans that expose them) from the Fireflies GraphQL API — the webhook only sends a meetingId.",
			},
			{
				key: "FIREFLIES_OWNER_EMAIL",
				label: "Fireflies owner email",
				placeholder: "you@company.com",
				help: "The existing user that ingested meetings are attributed to — webhooks carry no session, and this is a single-deploy app.",
			},
			{
				key: "FIREFLIES_RICH_INGEST",
				label: "Rich ingest",
				placeholder: "on (default)",
				help: "On by default: imports fetch the Fireflies summary, action items and analytics (filler / monologue / question chips, keywords, meeting type, attendee emails). Set to 0 to use the narrow transcript-only path.",
			},
			{
				key: "MEETING_PERF_COACH_NUDGE_HOUR",
				label: "Meeting coach nudge hour",
				placeholder: "9",
				restartRequired: true,
				help: 'Local hour (0–23) the weekly "time for a coaching session" email fires. Default 9 (9 AM). Read once at startup when the recurring job is registered — takes effect after a restart.',
			},
			{
				key: "MEETING_PERF_INGEST_HOUR",
				label: "Daily Fireflies catch-up hour",
				placeholder: "4",
				restartRequired: true,
				help: "Local hour (0–23) the daily Fireflies catch-up sweep runs — ingests the newest not-yet-imported meetings. Default 4 (4 AM, off-peak). Read once at startup when the recurring job is registered — takes effect after a restart.",
			},
			{
				key: "MEETING_PERF_INGEST_MAX_PER_RUN",
				label: "Catch-up max per run",
				placeholder: "100",
				help: "Max meetings ingested per daily catch-up run (newest-first, after dedup). Default 100. Each ingest also enqueues per-speaker AI scoring, so this caps the run's AI spend.",
			},
			{
				key: "MEETING_PERF_INTERNAL_DOMAIN",
				label: "Internal email domain",
				placeholder: "nymbl.app",
				help: "Speakers with this email domain are treated as 'internal'; everyone else 'external'. Blank falls back to the account owner's domain.",
			},
			{
				key: "MEETING_PERF_ANALYZE_MODEL",
				label: "Analysis model",
				options: MODEL_OPTIONS,
				help: `Model that scores each speaker across the six KPIs. ${MODEL_CAVEATS}`,
			},
			{
				key: "MEETING_PERF_CLASSIFY_MODEL",
				label: "Classification model",
				options: MODEL_OPTIONS,
				help: `Model that classifies a meeting's segment (when the heuristics can't). ${MODEL_CAVEATS}`,
			},
			{
				key: "MEETING_PERF_COACH_MODEL",
				label: "Coaching model",
				options: MODEL_OPTIONS,
				help: `Model that writes the longitudinal coaching session. ${MODEL_CAVEATS}`,
			},
		],
	},
};
